import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import LinksPage from "./components/LinksPage";
import ContentGenerator from "./components/ContentGenerator";
import ProgramsPage from "./components/ProgramsPage";
import AdminDashboard from "./components/AdminDashboard";
import MonetizationGuide from "./components/MonetizationGuide";
import SocialAutomation from "./components/SocialAutomation";
import InternationalMarketing from "./components/InternationalMarketing";
import InternationalMarketingGuide from "./components/InternationalMarketingGuide";
import SocialConnection from "./components/SocialConnection";
import APIConfiguration from "./components/APIConfiguration";
import QuickAPISetup from "./components/QuickAPISetup";
import RealTimeDashboard from "./components/RealTimeDashboard";
import ConnectionTest from "./components/ConnectionTest";
import SalesPage from "./components/SalesPage";
import UserManualPage from "./components/UserManualPage";
import VideoTutorialsPage from "./components/VideoTutorialsPage";
import OnboardingFlow from "./components/OnboardingFlow";
import ManualsPage from "./components/ManualsPage";
import LoginPage from "./components/LoginPage";
import ProtectedRoute from "./components/ProtectedRoute";
import ImageGenerator from "./components/ImageGenerator";
import VideoManager from "./components/VideoManager";
import CodeAuditor from "./components/CodeAuditor";
import UsageDashboard from "./components/UsageDashboard";
import InternalLinkInterceptor from "./components/InternalLinkInterceptor";
import UpgradePage from "./components/UpgradePage";
import CheckoutPage from "./components/CheckoutPage";
import CheckoutSuccessPage from "./components/CheckoutSuccessPage";
import UpsellPage from "./components/UpsellPage";
import { TrialBanner, useTrialCheck } from "./components/TrialBanner";
import AffiliateProgram from "./components/AffiliateProgram";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Imagem do MonetizeMaster v2.0
const MONETIZE_MASTER_IMAGE = "https://customer-assets.emergentagent.com/job_aff-monetize/artifacts/j3e8fg2l_WhatsApp%20Image%202025-12-21%20at%2018.53.42%20%281%29-fotor-enhance-2025122119750.jpg";

// Back Button Component - Para usar em todas as páginas
const BackButton = () => {
  const navigate = useNavigate();
  return (
    <button 
      onClick={() => navigate(-1)}
      className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
    >
      <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
      </svg>
      Voltar
    </button>
  );
};

// Navigation Component - Responsivo
const Navigation = ({ isAuthenticated, user, onLogout }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const navLinks = [
    { href: "/dashboard", label: "Painel", icon: "📊" },
    { href: "/real-dashboard", label: "Tempo Real", icon: "📈" },
    { href: "/setup", label: "APIs", icon: "🔧" },
    { href: "/links", label: "Links", icon: "🔗" },
    { href: "/content-generator", label: "IA", icon: "🤖" },
    { href: "/image-generator", label: "Imagens", icon: "🎨" },
    { href: "/video-manager", label: "Vídeos", icon: "📹" },
    { href: "/code-auditor", label: "Auditor", icon: "🔍" },
    { href: "/social-connect", label: "Social", icon: "📱" },
    { href: "/social-automation", label: "Automação", icon: "⚡" },
    { href: "/international", label: "Países", icon: "🌍" },
    { href: "/affiliate-program", label: "Afiliados", icon: "💎" },
    { href: "/monetization-guide", label: "Guia", icon: "💰" },
  ];

  return (
    <nav className="bg-white shadow-lg border-b sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-2 sm:px-4">
        <div className="flex justify-between h-14">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <a href="/" className="text-lg sm:text-xl font-bold">
              <span className="text-yellow-500">Monetize</span>
              <span className="text-orange-500">Master</span>
            </a>
          </div>

          {/* Desktop Navigation */}
          {isAuthenticated && (
            <div className="hidden lg:flex items-center space-x-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-gray-600 hover:text-blue-600 hover:bg-gray-100 px-2 py-1 rounded text-xs font-medium whitespace-nowrap"
                >
                  {link.icon} {link.label}
                </a>
              ))}
              
              {/* Dropdown Vendas */}
              <div className="relative group">
                <button className="text-yellow-600 hover:bg-gray-100 px-2 py-1 rounded text-xs font-medium flex items-center">
                  📚 Suporte ▼
                </button>
                <div className="absolute right-0 mt-1 w-40 bg-white rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 border">
                  <a href="/manuais" className="block px-3 py-2 text-xs hover:bg-gray-100">📚 Manuais</a>
                  <a href="/manual" className="block px-3 py-2 text-xs hover:bg-gray-100">📖 Manual</a>
                  <a href="/tutorials" className="block px-3 py-2 text-xs hover:bg-gray-100">🎥 Tutoriais</a>
                  <a href="/onboarding" className="block px-3 py-2 text-xs hover:bg-gray-100">🚀 Setup</a>
                </div>
              </div>

              {user?.is_admin && (
                <a href="/admin" className="text-red-600 hover:bg-red-50 px-2 py-1 rounded text-xs font-medium">
                  ⚙️ Admin
                </a>
              )}
            </div>
          )}

          {/* Right side - User info and actions */}
          <div className="flex items-center space-x-2">
            {isAuthenticated ? (
              <>
                {/* Status - hidden on small screens */}
                <div className="hidden sm:flex items-center space-x-1">
                  <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-green-600">Ativo</span>
                </div>
                
                {/* Email - truncated */}
                <span className="hidden md:block text-xs text-gray-500 max-w-[120px] truncate">
                  {user?.email}
                </span>
                
                {/* Logout button */}
                <button 
                  onClick={onLogout}
                  className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded text-xs font-medium"
                >
                  Sair
                </button>

                {/* Mobile menu button */}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="lg:hidden p-2 rounded hover:bg-gray-100"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {mobileMenuOpen ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    )}
                  </svg>
                </button>
              </>
            ) : (
              <a 
                href="/login" 
                className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-4 py-2 rounded font-semibold text-sm"
              >
                Login
              </a>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {isAuthenticated && mobileMenuOpen && (
          <div className="lg:hidden border-t py-2">
            <div className="grid grid-cols-4 gap-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="flex flex-col items-center p-2 text-gray-600 hover:bg-gray-100 rounded text-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="text-lg">{link.icon}</span>
                  <span className="text-xs">{link.label}</span>
                </a>
              ))}
            </div>
            <div className="grid grid-cols-4 gap-1 mt-1 pt-1 border-t">
              <a href="/manuais" className="flex flex-col items-center p-2 hover:bg-gray-100 rounded">
                <span className="text-lg">📚</span>
                <span className="text-xs">Manuais</span>
              </a>
              <a href="/tutorials" className="flex flex-col items-center p-2 hover:bg-gray-100 rounded">
                <span className="text-lg">🎥</span>
                <span className="text-xs">Tutoriais</span>
              </a>
              {user?.is_admin && (
                <a href="/admin" className="flex flex-col items-center p-2 hover:bg-red-50 rounded text-red-600">
                  <span className="text-lg">⚙️</span>
                  <span className="text-xs">Admin</span>
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Home Page (Public)
const Home = () => {
  const navigate = useNavigate();
  
  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          <h1 className="text-5xl font-extrabold text-gray-900 sm:text-6xl">
            Sistema REAL de 
            <span className="text-blue-600"> Marketing de Afiliados</span>
          </h1>
          <p className="mt-6 text-xl text-gray-600 max-w-3xl mx-auto">
            ✅ Integrações Reais | ✅ Tracking 100% Funcional | ✅ Pagamentos Automáticos<br/>
            9 Plataformas (ClickBank, Hotmart, Monetizze, Amazon, Shopee, Magalu, Mercado Livre, Awin, TikTok Shop) - Receita automatizada 24/7!
          </p>
          <div className="mt-10 flex flex-col md:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/login')}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-4 px-8 rounded-lg text-xl"
            >
              🚀 COMEÇAR AGORA (15min)
            </button>
            <button 
              onClick={() => navigate('/sales')}
              className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white font-bold py-4 px-8 rounded-lg text-xl"
            >
              💰 VER PÁGINA DE VENDAS
            </button>
            <button 
              onClick={() => navigate('/login')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg text-lg"
            >
              🔧 Setup Manual
            </button>
            <button 
              onClick={() => navigate('/login')}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-8 rounded-lg text-lg"
            >
              📈 Dashboard Real
            </button>
          </div>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <div className="text-3xl mb-4">🏦</div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">9 Plataformas Reais</h3>
            <p className="text-gray-600">
              ClickBank, Hotmart, Monetizze, Amazon, Shopee, Magazine Luiza, Mercado Livre, Awin e TikTok Shop com APIs reais configuradas.
            </p>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-lg">
            <div className="text-3xl mb-4">📊</div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Tracking em Tempo Real</h3>
            <p className="text-gray-600">
              Sistema de rastreamento avançado com métricas de cliques, conversões e comissões ao vivo.
            </p>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-lg">
            <div className="text-3xl mb-4">💳</div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Pagamentos Automáticos</h3>
            <p className="text-gray-600">
              PIX (Brasil), Stripe e PayPal processam suas comissões automaticamente.
            </p>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-lg">
            <div className="text-3xl mb-4">🤖</div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">IA + Automação Total</h3>
            <p className="text-gray-600">
              Conteúdo gerado por IA, postagens automáticas e otimização contínua.
            </p>
          </div>
        </div>

        <div className="mt-20 bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
            🚀 Sistema Real - Comece a Lucrar Agora!
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="text-4xl mb-4">1️⃣</div>
              <h4 className="font-bold text-lg mb-2">Crie sua Conta</h4>
              <p className="text-sm text-gray-600 mb-4">
                Cadastre-se em menos de 2 minutos e escolha seu plano
              </p>
              <button 
                onClick={() => navigate('/login')}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                🔧 Criar Conta
              </button>
            </div>
            <div className="text-center p-6 bg-green-50 rounded-lg">
              <div className="text-4xl mb-4">2️⃣</div>
              <h4 className="font-bold text-lg mb-2">Configure suas APIs</h4>
              <p className="text-sm text-gray-600 mb-4">
                Conecte suas contas de afiliado em 9 plataformas
              </p>
              <button 
                onClick={() => navigate('/login')}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
              >
                📈 Configurar
              </button>
            </div>
            <div className="text-center p-6 bg-yellow-50 rounded-lg">
              <div className="text-4xl mb-4">3️⃣</div>
              <h4 className="font-bold text-lg mb-2">Comece a Ganhar</h4>
              <p className="text-sm text-gray-600 mb-4">
                Deixe o sistema trabalhar por você 24/7
              </p>
              <button 
                onClick={() => navigate('/login')}
                className="bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700"
              >
                💰 Começar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Dashboard Component
const Dashboard = ({ user }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setDashboardData({
        stats: { total_links: 0, total_clicks: 0, total_conversions: 0, total_earnings: 0.0, conversion_rate: 0 },
        recent_links: [],
        recent_content: [],
        subscription_plan: user?.subscription_plan || "starter",
        is_admin: user?.is_admin || false
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard - Bem-vindo, {user?.name || user?.email}!</h1>
        <p className="text-gray-600">Plano: {user?.subscription_plan?.toUpperCase()} | {user?.is_admin ? '⚙️ Administrador' : '👤 Usuário'}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-md">
              <div className="text-blue-600 text-2xl">🔗</div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Links</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_links || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-md">
              <div className="text-green-600 text-2xl">👆</div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Clicks</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_clicks || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-md">
              <div className="text-purple-600 text-2xl">🎯</div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Conversões</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_conversions || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-md">
              <div className="text-yellow-600 text-2xl">💰</div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Ganhos</p>
              <p className="text-2xl font-bold text-gray-900">R$ {(stats.total_earnings || 0).toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">🚀 Ações Rápidas</h3>
          <div className="space-y-3">
            <a href="/social-connect" className="block w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md text-center">
              🔗 Conectar APIs Sociais
            </a>
            <a href="/links" className="block w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md text-center">
              ➕ Criar Novo Link
            </a>
            <a href="/content-generator" className="block w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-md text-center">
              🤖 Gerar Conteúdo IA
            </a>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 Status do Sistema</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">IA de Conteúdo</span>
              <span className="text-green-600 font-semibold">✅ Ativo</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Links Automáticos</span>
              <span className="text-green-600 font-semibold">✅ Ativo</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">9 Plataformas</span>
              <span className="text-green-600 font-semibold">✅ Ativo</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Dashboard em Tempo Real</span>
              <span className="text-green-600 font-semibold">✅ Ativo</span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">💡 Próximos Passos</h3>
          <div className="space-y-2 text-sm">
            <div className="p-2 bg-yellow-50 rounded text-yellow-800">
              <strong>1.</strong> Conecte suas APIs sociais
            </div>
            <div className="p-2 bg-blue-50 rounded text-blue-800">
              <strong>2.</strong> Crie seus primeiros links
            </div>
            <div className="p-2 bg-green-50 rounded text-green-800">
              <strong>3.</strong> Deixe o sistema lucrar automaticamente!
            </div>
          </div>
        </div>
      </div>

      {/* Usage Dashboard - Mostra limites e uso */}
      <div className="mb-8">
        <UsageDashboard />
      </div>
    </div>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);

  // Função para verificar e validar token com o backend
  const validateToken = async (token) => {
    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await axios.get(`${API}/auth/me`);
      return response.data;
    } catch (error) {
      console.error('Token validation failed:', error);
      return null;
    }
  };

  // Inicialização única da autenticação
  useEffect(() => {
    let isMounted = true;
    
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      
      if (token && savedUser) {
        try {
          // Primeiro restaurar estado do localStorage para UI rápida
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const parsedUser = JSON.parse(savedUser);
          
          if (isMounted) {
            setUser(parsedUser);
            setIsAuthenticated(true);
          }
          
          // Validar token apenas uma vez na inicialização
          const validatedUser = await validateToken(token);
          
          if (!isMounted) return;
          
          if (validatedUser) {
            // Atualizar dados do usuário se necessário
            const updatedUser = { ...parsedUser, ...validatedUser };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
          } else {
            // Token inválido - fazer logout silencioso
            console.warn('Token expired or invalid, logging out...');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            delete axios.defaults.headers.common['Authorization'];
            setIsAuthenticated(false);
            setUser(null);
          }
        } catch (error) {
          console.error('Auth init error:', error);
          if (isMounted) {
            // Manter autenticado com dados locais se falhar validação
            // (pode ser erro de rede temporário)
            const parsedUser = JSON.parse(savedUser);
            setUser(parsedUser);
            setIsAuthenticated(true);
          }
        }
      }
      
      if (isMounted) {
        setLoading(false);
        setAuthChecked(true);
      }
    };
    
    initAuth();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Interceptor para lidar com erros 401 globalmente - só executar após auth estar verificada
  useEffect(() => {
    if (!authChecked) return;
    
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Só fazer logout automático para 401 se for erro de autenticação real
        // (não para erros de rede ou outros problemas)
        if (error.response?.status === 401 && 
            error.config?.url?.includes('/api/') &&
            !error.config?.url?.includes('/auth/login')) {
          console.warn('Auth error detected, redirecting to login...');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete axios.defaults.headers.common['Authorization'];
          setIsAuthenticated(false);
          setUser(null);
          // Usar setTimeout para evitar race condition
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [authChecked]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    setUser(null);
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster position="top-right" />
      <BrowserRouter>
        <InternalLinkInterceptor />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<><Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} /><Home /></>} />
          <Route path="/login" element={
            isAuthenticated && user?.payment_status === 'paid' ? <Navigate to="/dashboard" /> : 
            <LoginPage setIsAuthenticated={setIsAuthenticated} setUser={setUser} />
          } />
          <Route path="/sales" element={<SalesPage />} />
          
          {/* Protected Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <TrialBanner user={user} />
              <Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
              <Dashboard user={user} />
            </ProtectedRoute>
          } />
          <Route path="/real-dashboard" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <TrialBanner user={user} />
              <Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
              <RealTimeDashboard />
            </ProtectedRoute>
          } />
          <Route path="/setup" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <TrialBanner user={user} />
              <Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
              <QuickAPISetup />
            </ProtectedRoute>
          } />
          <Route path="/setup-apis" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <TrialBanner user={user} />
              <Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
              <QuickAPISetup />
            </ProtectedRoute>
          } />
          <Route path="/connection-test" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
              <ConnectionTest />
            </ProtectedRoute>
          } />
          <Route path="/links" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
              <LinksPage />
            </ProtectedRoute>
          } />
          <Route path="/upgrade" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <UpgradePage />
            </ProtectedRoute>
          } />
          <Route path="/checkout" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requirePayment={false}>
              <CheckoutPage />
            </ProtectedRoute>
          } />
          <Route path="/checkout/success" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requirePayment={false}>
              <CheckoutSuccessPage />
            </ProtectedRoute>
          } />
          <Route path="/upsell" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requirePayment={false}>
              <UpsellPage />
            </ProtectedRoute>
          } />
          <Route path="/content-generator" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
              <ContentGenerator />
            </ProtectedRoute>
          } />
          <Route path="/image-generator" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
              <ImageGenerator />
            </ProtectedRoute>
          } />
          <Route path="/video-manager" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
              <VideoManager />
            </ProtectedRoute>
          } />
          <Route path="/code-auditor" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
              <CodeAuditor />
            </ProtectedRoute>
          } />
          <Route path="/social-automation" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
              <SocialAutomation />
            </ProtectedRoute>
          } />
          <Route path="/social-connect" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
              <SocialConnection />
            </ProtectedRoute>
          } />
          <Route path="/international" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
              <InternationalMarketing />
            </ProtectedRoute>
          } />
          <Route path="/programs" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
              <ProgramsPage />
            </ProtectedRoute>
          } />
          <Route path="/monetization-guide" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
              <MonetizationGuide />
            </ProtectedRoute>
          } />
          <Route path="/international-guide" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
              <InternationalMarketingGuide />
            </ProtectedRoute>
          } />
          <Route path="/manual" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
              <UserManualPage />
            </ProtectedRoute>
          } />
          <Route path="/tutorials" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
              <VideoTutorialsPage />
            </ProtectedRoute>
          } />
          <Route path="/onboarding" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
              <OnboardingFlow />
            </ProtectedRoute>
          } />
          <Route path="/manuais" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
              <ManualsPage />
            </ProtectedRoute>
          } />
          <Route path="/affiliate-program" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>
              <Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
              <AffiliateProgram />
            </ProtectedRoute>
          } />
          
          {/* Admin Route */}
          <Route path="/admin" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requireAdmin={true}>
              <Navigation isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout} />
              <AdminDashboard />
            </ProtectedRoute>
          } />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export { BackButton };
export default App;
